import { useState, useEffect } from "react";
import { ChevronDown, Play, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const images = [
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/iw1xs7zq_IMG_2390.jpeg", bw: true },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/zag9noy8_IMG_2387.jpeg", bw: true },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/roe2lh7p_IMG_2389.jpeg", bw: true },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/lqadewpl_IMG_2391.jpeg", bw: true },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/v4dhqwgy_IMG_1477.jpeg", bw: false },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/tce4wmbe_IMG_1545.jpeg", bw: false },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/w7k7izow_IMG_2308.jpeg", bw: false },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/m7llk23k_IMG_2309.jpeg", bw: false },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/4urp6c9n_IMG_2310.jpeg", bw: false },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/ibjfciaq_79235855_2948695395154333_6835955203414425600_n%20%282%29.jpg", bw: false },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/aa2ip6g6_167393644_3805964839480669_2297204634654180317_n.jpg", bw: false },
    { url: "https://customer-assets.emergentagent.com/job_faith-media-hub/artifacts/v5taucde_168466908_3805965256147294_2721448358427313852_n.jpg", bw: false },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [images.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <section className="hero-section" data-testid="hero-section">
      {/* Background Image Carousel */}
      {images.map((image, index) => (
        <div
          key={index}
          className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
            index === currentSlide ? "opacity-100" : "opacity-0"
          } ${image.bw ? "grayscale" : ""}`}
          style={{ backgroundImage: `url('${image.url}')`, filter: image.bw ? "grayscale(100%)" : "none" }}
          data-testid={`hero-bg-${index}`}
        />
      ))}
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Carousel Navigation */}
      <button
        onClick={prevSlide}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
        data-testid="carousel-prev"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/20 backdrop-blur-sm flex items-center justify-center text-white hover:bg-white/30 transition-colors"
        data-testid="carousel-next"
      >
        <ChevronRight size={24} />
      </button>

      {/* Slide Indicators */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex gap-2" data-testid="carousel-indicators">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 transition-all ${
              index === currentSlide ? "bg-[#FFB800] w-8" : "bg-white/50"
            }`}
            data-testid={`carousel-indicator-${index}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full">
        <div className="max-w-4xl">
          {/* Overline */}
          <p className="text-overline text-white/80 mb-6 animate-fade-in-up opacity-0" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }} data-testid="hero-overline">
            Welcome to Light City Church
          </p>

          {/* Main Heading */}
          <h1 className="heading-massive text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] text-white mb-8 animate-fade-in-up opacity-0" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }} data-testid="hero-heading">
            Bringing<br />
            Light to<br />
            the City
          </h1>

          {/* Tagline */}
          <p className="font-['Satoshi'] text-lg md:text-xl text-white/90 mb-10 max-w-xl leading-relaxed animate-fade-in-up opacity-0" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }} data-testid="hero-tagline">
            A gospel-centric community in New Orleans dedicated to transforming lives through faith, worship, and authentic discipleship.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up opacity-0" style={{ animationDelay: '400ms', animationFillMode: 'forwards' }}>
            <a href="#visit">
              <Button
                className="btn-sharp bg-white text-black hover:bg-transparent hover:text-white hover:shadow-[inset_0_0_0_2px_white] px-8 py-6 text-sm w-full sm:w-auto"
                data-testid="hero-plan-visit-btn"
              >
                Plan Your Visit
              </Button>
            </a>
            <a href="https://www.youtube.com/live/5meTLBPUsc0?is=x4x0HcFq0VSvmggX" target="_blank" rel="noopener noreferrer">
              <Button
                variant="outline"
                className="btn-sharp border-white text-white hover:bg-white hover:text-black px-8 py-6 text-sm w-full sm:w-auto flex items-center gap-2"
                data-testid="hero-watch-live-btn"
              >
                <Play size={18} />
                Watch Latest Message
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white scroll-indicator z-10" data-testid="scroll-indicator">
        <ChevronDown size={32} />
      </div>
    </section>
  );
};

export default Hero;
